import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { ArrowLeft, BellRing, Download, Pencil, UserPlus } from 'lucide-react';
import { COMM_DEPARTMENT_LABEL, formatDateTime } from '@nu/shared';
import { api } from '../../api/client.js';
import Modal from '../../components/Modal.jsx';
import { problem } from '../bookings/util.js';
import MessageBlocks from './MessageBlocks.jsx';
import RecipientPicker, { NO_TARGETS } from './RecipientPicker.jsx';

// One sent message: the figures, reads over time, everyone it went to, and
// what can be done next (chase, send to more people, correct it, export).
export default function CommDetail() {
  const { id } = useParams();
  const [c, setC] = useState(null);
  const [error, setError] = useState(null);
  const [fresh, setFresh] = useState([]);
  const [show, setShow] = useState('all');
  const [picked, setPicked] = useState([]);
  const [note, setNote] = useState(null);
  const [adding, setAdding] = useState(false);
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => {
    api(`/comms/${id}`).then(setC, (e) => setError(problem(e)));
    api(`/comms/${id}/new-members`).then(setFresh, () => setFresh([]));
  }, [id]);
  useEffect(load, [load]);

  const outstanding = useCallback((r) => (c?.requiresAck ? !r.ackedAt : !r.readAt), [c]);
  const rows = useMemo(() => (c?.recipients || []).filter((r) => (show === 'all' ? true : show === 'unread' ? !r.readAt : !r.ackedAt)), [c, show]);

  if (!c) return <section className="card">{error ? <p className="notice-line notice-bad">{error}</p> : <p className="muted">Loading…</p>}</section>;
  const total = c.recipients.length;
  const read = c.recipients.filter((r) => r.readAt).length;
  const acked = c.recipients.filter((r) => r.ackedAt).length;
  const waiting = c.recipients.filter(outstanding);
  const pct = (n) => (total ? Math.round((n / total) * 100) : 0);

  const remind = async (personIds) => {
    setBusy(true);
    setNote(null);
    try {
      const r = await api(`/comms/${id}/remind`, { method: 'POST', body: personIds ? { personIds } : {} });
      setNote({ tone: 'ok', text: r.reminded ? `Reminder sent to ${r.reminded} ${r.reminded === 1 ? 'person' : 'people'}.` : 'Nobody needed a reminder.' });
      setPicked([]);
      load();
    } catch (e) {
      setNote({ tone: 'bad', text: problem(e) });
    } finally {
      setBusy(false);
    }
  };
  const add = async (personIds, sendEmail) => {
    setBusy(true);
    setNote(null);
    try {
      const r = await api(`/comms/${id}/recipients`, { method: 'POST', body: { personIds, sendEmail } });
      setNote({ tone: 'ok', text: `Sent to ${r.added} more ${r.added === 1 ? 'person' : 'people'}.` });
      setAdding(false);
      load();
    } catch (e) {
      setNote({ tone: 'bad', text: problem(e) });
    } finally {
      setBusy(false);
    }
  };

  const status = (r) => (r.ackedAt ? 'Acknowledged' : r.readAt ? (c.requiresAck ? 'Read, not acknowledged' : 'Read') : 'Not read');
  const csv = () => {
    const BOM = String.fromCharCode(0xfeff); // so Excel reads it as UTF-8
    const cell = (v) => `"${String(v ?? '').replace(/"/g, '""')}"`;
    const lines = [['Name', 'Email', 'Status', 'Read', 'Acknowledged', 'Reminders'].map(cell).join(',')]
      .concat(c.recipients.map((r) => [r.name, r.email, status(r), r.readAt ? formatDateTime(r.readAt) : '', r.ackedAt ? formatDateTime(r.ackedAt) : '', r.reminders].map(cell).join(',')));
    const a = document.createElement('a');
    a.href = URL.createObjectURL(new Blob([BOM + lines.join(String.fromCharCode(13, 10))], { type: 'text/csv' }));
    a.download = `message-${c.id}-${c.subject.replace(/[^\w]+/g, '-').slice(0, 40)}.csv`;
    a.click();
    setTimeout(() => URL.revokeObjectURL(a.href), 1000);
  };

  return (
    <div className="stack">
      <section className="card">
        <Link to="/comms" className="link-btn back-link"><ArrowLeft size={18} aria-hidden="true" /> Messages sent</Link>
        <div className="page-head">
          <div>
            <p className="msg-dept">{COMM_DEPARTMENT_LABEL[c.department]}{c.priority === 'urgent' && <span className="status status-aborted msg-urgent">Urgent</span>}</p>
            <h1>{c.subject}</h1>
            <p className="small muted">
              To {c.audience} · sent by {c.sentByName} on {formatDateTime(c.sentAt)}
              {c.sendEmail ? ' · emailed a link' : ''}
              {c.editedAt ? ` · corrected ${formatDateTime(c.editedAt)} by ${c.editedByEmail}` : ''}
            </p>
          </div>
          <div className="detail-actions">
            <Link to={`/comms/${id}/edit`} className="btn with-icon"><Pencil size={16} aria-hidden="true" /> Correct it</Link>
            <button type="button" className="btn with-icon" onClick={() => setAdding(true)}><UserPlus size={16} aria-hidden="true" /> Send to more people</button>
            <button type="button" className="btn with-icon" onClick={csv}><Download size={16} aria-hidden="true" /> Export CSV</button>
          </div>
        </div>
        <div className="stat-row comm-stats">
          <div className="stat"><span className="stat-num">{total}</span><span className="stat-label">Sent to</span></div>
          <div className="stat"><span className="stat-num">{pct(read)}%</span><span className="stat-label">Read ({read})</span></div>
          {c.requiresAck && <div className="stat"><span className="stat-num">{pct(acked)}%</span><span className="stat-label">Acknowledged ({acked})</span></div>}
          <div className={`stat${waiting.length ? ' stat-warn' : ''}`}><span className="stat-num">{waiting.length}</span><span className="stat-label">{c.requiresAck ? 'Still to acknowledge' : 'Still to read'}</span></div>
        </div>
        <ReadsChart comm={c} />
        {fresh.length > 0 && (
          <p className="notice-line notice-warn comm-fresh">
            {fresh.length} {fresh.length === 1 ? 'person has' : 'people have'} joined {c.audience} since this was sent.{' '}
            <button type="button" className="link-btn" disabled={busy} onClick={() => add(fresh.map((p) => p.id), c.sendEmail)}>Send it to {fresh.length === 1 ? 'them' : `all ${fresh.length}`}</button>
          </p>
        )}
        {note && <p className={`notice-line notice-${note.tone}`} role="status">{note.text}</p>}
      </section>

      <section className="card">
        <div className="page-head">
          <h2>Who it went to</h2>
          <div className="detail-actions">
            {picked.length > 0 && <button type="button" className="btn with-icon" disabled={busy} onClick={() => remind(picked)}><BellRing size={16} aria-hidden="true" /> Remind selected ({picked.length})</button>}
            <button type="button" className="btn primary with-icon" disabled={busy || !waiting.length} onClick={() => remind(null)}><BellRing size={16} aria-hidden="true" /> Remind all {waiting.length} outstanding</button>
          </div>
        </div>
        <div className="chips comm-filter">
          {[['all', `All (${total})`], ['unread', `Not read (${total - read})`], ...(c.requiresAck ? [['unacked', `Not acknowledged (${total - acked})`]] : [])].map(([k, label]) => (
            <button key={k} type="button" className={`chip${show === k ? ' on' : ''}`} aria-pressed={show === k} onClick={() => setShow(k)}>{label}</button>
          ))}
        </div>
        <div className="table-scroll">
          <table className="table">
            <thead>
              <tr><th className="pick-col"><span className="sr-only">Select</span></th><th>Name</th><th>Status</th><th>Read</th>{c.requiresAck && <th>Acknowledged</th>}<th>Reminders</th></tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.personId}>
                  <td className="pick-col">
                    {outstanding(r) && <input type="checkbox" aria-label={`Select ${r.name}`} checked={picked.includes(r.personId)} onChange={() => setPicked((p) => (p.includes(r.personId) ? p.filter((x) => x !== r.personId) : [...p, r.personId]))} />}
                  </td>
                  <td><strong>{r.name}</strong><div className="small muted">{r.email}</div></td>
                  <td><span className={`status ${r.ackedAt || (!c.requiresAck && r.readAt) ? 'status-completed' : r.readAt ? 'status-on_the_way' : 'status-cancelled'}`}>{status(r)}</span></td>
                  <td>{r.readAt ? formatDateTime(r.readAt) : <span className="muted">-</span>}</td>
                  {c.requiresAck && <td>{r.ackedAt ? formatDateTime(r.ackedAt) : <span className="muted">-</span>}</td>}
                  <td>{r.reminders ? `${r.reminders} (last ${formatDateTime(r.remindedAt)})` : <span className="muted">None</span>}</td>
                </tr>
              ))}
              {!rows.length && <tr><td colSpan={6} className="muted">Nobody here.</td></tr>}
            </tbody>
          </table>
        </div>
      </section>

      <section className="card">
        <h2>The message</h2>
        <div className="msg-reader comm-preview"><MessageBlocks blocks={c.blocks} /></div>
      </section>

      {adding && <AddPeople comm={c} busy={busy} onClose={() => setAdding(false)} onAdd={add} />}
    </div>
  );
}

function AddPeople({ comm, busy, onClose, onAdd }) {
  const [audience, setAudience] = useState(null);
  const [targets, setTargets] = useState(NO_TARGETS);
  const [email, setEmail] = useState(comm.sendEmail);
  useEffect(() => { api('/comms/audience').then(setAudience, () => setAudience({ people: [], roles: [], teams: [] })); }, []);
  const n = targets.people.length;
  return (
    <Modal
      open
      wide
      title="Send to more people"
      onClose={onClose}
      footer={(
        <>
          <label className="check modal-foot-check"><input type="checkbox" checked={email} onChange={(e) => setEmail(e.target.checked)} /> Email them a link</label>
          <button type="button" className="btn" onClick={onClose}>Cancel</button>
          <button type="button" className="btn primary" disabled={!n || busy} onClick={() => onAdd(targets.people, email)}>Send to {n || ''} {n === 1 ? 'person' : 'people'}</button>
        </>
      )}
    >
      <p className="small muted">People it already went to aren&apos;t listed.</p>
      <RecipientPicker audience={audience} targets={targets} onChange={setTargets} peopleOnly exclude={comm.recipients.map((r) => r.personId)} />
    </Modal>
  );
}

// Reads (and acknowledgements) adding up since it was sent.
function ReadsChart({ comm }) {
  const total = comm.recipients.length;
  const reads = comm.recipients.map((r) => r.readAt).filter(Boolean).map((t) => Date.parse(t)).sort((a, b) => a - b);
  if (!total || !reads.length) return null;
  const acks = comm.recipients.map((r) => r.ackedAt).filter(Boolean).map((t) => Date.parse(t)).sort((a, b) => a - b);
  const start = Date.parse(comm.sentAt);
  const end = Math.max(Date.now(), reads[reads.length - 1]);
  const W = 640;
  const H = 140;
  const x = (t) => ((t - start) / Math.max(1, end - start)) * W;
  const y = (n) => H - (n / total) * H;
  const path = (times) => {
    let d = `M0,${H}`;
    times.forEach((t, i) => { d += ` L${x(t).toFixed(1)},${y(i).toFixed(1)} L${x(t).toFixed(1)},${y(i + 1).toFixed(1)}`; });
    return `${d} L${W},${y(times.length).toFixed(1)}`;
  };
  return (
    <figure className="reads-chart">
      <svg viewBox={`0 0 ${W} ${H}`} preserveAspectRatio="none" role="img" aria-label={`${reads.length} of ${total} have read it`}>
        <line x1="0" y1="0" x2={W} y2="0" className="reads-grid" />
        <path d={`${path(reads)} L${W},${H} Z`} className="reads-area" />
        <path d={path(reads)} className="reads-line" />
        {comm.requiresAck && acks.length > 0 && <path d={path(acks)} className="acks-line" />}
      </svg>
      <figcaption className="small muted">
        <span>{formatDateTime(comm.sentAt)}</span>
        <span><span className="key key-read" /> Read{comm.requiresAck && <> <span className="key key-ack" /> Acknowledged</>}</span>
        <span>Now</span>
      </figcaption>
    </figure>
  );
}
